
  import { defineComponent, PropType } from 'vue';

  import InputBlock from '@/common/components/redesigned/InputBlock.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import Constants from '@/common/constants';

  import Api from '../api';
  import type { TPresentationForm, TPresentationOption } from '../types/presentation';
  import { PRESENTATION_FILE_TYPE_OPTIONS } from '../utils/constants';
  import PresentationMainForm from './PresentationMainForm.vue';

  type TOnePageForm = Pick<TPresentationForm, 'countryId' | 'year' | 'fileType'>;

  const EXCLUDED_COUNTRIES = [Constants.countryCubaId, Constants.countryDPRKId];

  export default defineComponent({
    name: 'PresentationOnePageForm',
    components: { PresentationMainForm, SelectMultiComponent, InputBlock },
    props: {
      countries: {
        type: Array as PropType<TPresentationOption[]>,
        default: () => [],
      },
      defaultCountryId: {
        type: [Number, String] as PropType<Nullable<number | string>>,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['success', 'loading'],
    data(): { form: TOnePageForm; fileTypeOptions: TPresentationOption[]; isLoading: boolean } {
      return {
        form: {
          countryId: this.defaultCountryId,
          year: null,
          fileType: null,
        },
        fileTypeOptions: PRESENTATION_FILE_TYPE_OPTIONS,
        isLoading: false,
      };
    },
    computed: {
      fileTypeModel: {
        get(): Exclude<TOnePageForm['fileType'], null> | undefined {
          return this.form.fileType ?? undefined;
        },
        set(value: TOnePageForm['fileType'] | undefined) {
          this.form.fileType = value ?? null;
        },
      },
      getFilteredCountries(): TPresentationOption[] {
        return this.countries.filter((country) => !EXCLUDED_COUNTRIES.includes(Number(country.id)));
      },
    },
    methods: {
      async onDownload() {
        const fileTypeSelect = this.$refs.fileTypeSelect as InstanceType<typeof SelectMultiComponent> | undefined;
        fileTypeSelect?.v$.$touch();
        if (fileTypeSelect?.v$.$invalid) return;

        this.setLoading(true);
        try {
          await Api.createOnePagePresentation(this.form);
          this.$emit('success');
        } catch {
          Constants.alert.fire(Constants.errorTitle, Constants.commonError, 'error');
        } finally {
          this.setLoading(false);
        }
      },
      setLoading(value: boolean) {
        this.isLoading = value;
        this.$emit('loading', value);
      },
    },
  });
